import React, { useEffect, useState, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import HexGrid from './HexGrid';
import HexGridRect from './HexGridRect';
import ControlPanel from './ControlPanel';
import LandingPage from './LandingPage';

// ── Socket connection ─────────────────────────────────────────────────────────
const BACKEND_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:5001' : '';
const socket = io(BACKEND_URL, { transports: ['websocket', 'polling'] });

// ── Color options for the context menu ───────────────────────────────────────
const COLOR_OPTIONS = [
  { id: 'focus',  label: '📍 إشارة استعداد (نبض أحمر)', hex: '#FF0000', isFocus: true },
  { id: 'orange', label: 'البرتقالي', hex: '#FF8C00' },
  { id: 'green',  label: 'الأخضر',    hex: '#2E8B57' },
  { id: 'blue',   label: 'الأزرق',    hex: '#1E88E5' },
  { id: 'purple', label: 'البنفسجي',  hex: '#8A2BE2' },
  { id: 'white',  label: 'إزالة اللون (أبيض)', hex: '#FAFAF8', isWhite: true },
];

const TEAM_DISPLAY = {
  focus:  'إشارة استعداد',
  orange: 'البرتقالي',
  green:  'الأخضر',
  blue:   'الأزرق',
  purple: 'البنفسجي',
  white:  null,
};

// Grid size configs handled in ControlPanel now

export default function App() {
  const [roomId, setRoomId]       = useState(() => new URLSearchParams(window.location.search).get('room'));
  const [grid, setGrid]           = useState(null);
  const [scores, setScores]       = useState({ orange: 0, green: 0, blue: 0 });
  const [connected, setConnected] = useState(false);
  const [isAdmin, setIsAdmin]     = useState(false);
  const [winEvent, setWinEvent]   = useState(null);
  const [winPath, setWinPath]     = useState([]);
  const [ctxMenu, setCtxMenu]     = useState(null);
  const [gridRadius, setGridRadius] = useState(3);
  const [mode, setMode]             = useState('3p');
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [buzzerWinner, setBuzzerWinner] = useState(null);
  const [myTeam, setMyTeam]         = useState(null);
  
  const isPlayer = new URLSearchParams(window.location.search).get('buzzer') === 'true';

  // PIN modal state
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinInput, setPinInput] = useState('');

  const ctxRef = useRef(null);

  // ── Join Room ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (roomId && connected) {
      socket.emit('join_room', { roomId }, () => {
        // Automatically verify PIN if it is stored in localStorage
        const storedPin = localStorage.getItem(`pin_${roomId}`);
        if (storedPin) {
          socket.emit('verify_pin', { pin: storedPin }, (res) => {
            if (res && res.success && res.isValid) {
              setIsAdmin(true);
            } else {
              localStorage.removeItem(`pin_${roomId}`);
            }
          });
        }
      });
    }
  }, [roomId, connected]);

  // ── Socket events ───────────────────────────────────────────────────────────
  useEffect(() => {
    socket.on('connect',    () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));

    socket.on('game_state', ({ grid, scores, radius, mode, buzzerWinner }) => {
      if (grid) setGrid(grid);
      if (scores) setScores(scores);
      if (radius) setGridRadius(radius);
      if (mode) setMode(mode);
      setBuzzerWinner(buzzerWinner || null);
    });

    socket.on('cell_updated', ({ grid }) => {
      if (grid) setGrid(grid);
    });

    socket.on('win_event', ({ team, path, scores }) => {
      setWinPath(path || []);
      if (scores) setScores(scores);
      setWinEvent({ team });
    });

    socket.on('buzzer_winner', ({ team }) => {
      setBuzzerWinner(team);
    });

    socket.on('buzzer_reset', () => {
      setBuzzerWinner(null);
    });

    socket.on('game_reset', ({ grid, scores, radius, mode }) => {
      if (grid) setGrid(grid);
      if (scores) setScores(scores);
      if (radius) setGridRadius(radius);
      if (mode) setMode(mode);
      setWinEvent(null);
      setWinPath([]);
      setBuzzerWinner(null);
    });

    socket.on('error', (err) => {
      if (err.message && err.message !== 'Unauthorized: Admin PIN required') {
        alert(err.message);
      }
    });

    return () => socket.removeAllListeners();
  }, []);

  // ── Close context menu on outside click ──────────────────────────────────
  useEffect(() => {
    const handleClick = (e) => {
      if (ctxRef.current && !ctxRef.current.contains(e.target)) {
        setCtxMenu(null);
      }
    };
    window.addEventListener('mousedown', handleClick);
    return () => window.removeEventListener('mousedown', handleClick);
  }, []);

  // ── Admin Handlers ───────────────────────────────────────────────────────
  const handlePinSubmit = (e) => {
    e.preventDefault();
    socket.emit('verify_pin', { pin: pinInput }, (res) => {
      if (res && res.success && res.isValid) {
        setIsAdmin(true);
        setShowPinModal(false);
        setPinInput('');
        localStorage.setItem(`pin_${roomId}`, pinInput);
        setShowSettingsModal(true); // Automatically open settings once authed
      } else {
        alert('الرقم السري غير صحيح!');
      }
    });
  };

  // ── Cell click handler ───────────────────────────────────────────────────
  const handleCellClick = useCallback((e, cellKey, cell) => {
    if (!isAdmin) return;
    e.stopPropagation();
    const color = cell?.color || 'white';

    // White → directly set to focus (red), no menu
    if (color === 'white') {
      socket.emit('color_cell', { cellKey, color: 'focus' });
      return;
    }

    // Otherwise show the context menu
    const menuHeight = 350;
    const menuWidth = 220;
    const isNearBottom = e.clientY + menuHeight > window.innerHeight;
    const isNearRight  = e.clientX + menuWidth  > window.innerWidth;
    setCtxMenu({
      x: e.clientX,
      y: e.clientY,
      isNearBottom,
      isNearRight,
      cellKey,
      currentColor: color
    });
  }, [isAdmin]);

  // ── Color selection from context menu ────────────────────────────────────
  const handleColorSelect = useCallback((color) => {
    if (!ctxMenu) return;
    socket.emit('color_cell', { cellKey: ctxMenu.cellKey, color });
    setCtxMenu(null);
  }, [ctxMenu]);

  // ── Game reset ───────────────────────────────────────────────────────────
  const handleResetColors = useCallback((shuffle) => {
    socket.emit('reset_colors', { shuffle });
  }, []);

  const handleResetGame = useCallback(() => {
    if (window.confirm('هل تريد إعادة شاملة للعبة (مسح الألوان + توزيع حروف جديدة)؟')) {
      socket.emit('reset_game');
    }
  }, []);

  const handleChangeGridSize = useCallback((newRadius) => {
    if (window.confirm('تغيير حجم الشبكة سيمسح الحروف والألوان الحالية لتوليد شبكة جديدة، هل أنت متأكد؟ (لن يتم تصفير النتيجة)')) {
      socket.emit('set_grid_radius', { radius: newRadius });
    }
  }, []);

  // ── Room Navigation ──────────────────────────────────────────────────────
  const handleJoinRoom = (id) => {
    const url = new URL(window.location);
    url.searchParams.set('room', id);
    window.history.pushState({}, '', url);
    setRoomId(id);
  };

  const handleCreateRoom = (data, cb) => {
    socket.emit('create_room', data, cb);
  };

  const leaveRoom = () => {
    if (window.confirm('هل أنت متأكد أنك تريد مغادرة هذه الجلسة والعودة للرئيسية؟')) {
      const url = new URL(window.location);
      url.searchParams.delete('room');
      window.history.pushState({}, '', url);
      setRoomId(null);
      setIsAdmin(false);
    }
  };

  // ── Render Landing Page ──────────────────────────────────────────────────
  if (!roomId) {
    return <LandingPage onJoinRoom={handleJoinRoom} onCreateRoom={handleCreateRoom} />;
  }

  // ── Winner team names ────────────────────────────────────────────────────
  const teamDisplayName = {
    orange: 'فريق البرتقال 🟠',
    green:  'فريق الأخضر 🟢',
    blue:   'فريق الأزرق 🔵',
  };

  const currentCellColorName = ctxMenu ? TEAM_DISPLAY[ctxMenu.currentColor] : null;

  const validColorOptions = COLOR_OPTIONS.filter(o => {
    if (mode === '2p' && (o.id === 'blue' || o.id === 'purple')) return false;
    if (mode === '3p' && o.id === 'purple') return false;
    return true;
  });

  return (
    <div className="app-container">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <header className="app-header">
        
        {/* Right Side: Title & Room Info */}
        <div className="header-right">
          <h1>حروف</h1>
          
          <div className="status-bar" style={{ display: 'flex', gap: 12, alignItems: 'center', background: 'rgba(255,255,255,0.03)', padding: '6px 16px', borderRadius: 12, margin: 0, border: 'none' }}>
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>رقم الجلسة</span>
              <strong style={{ color: '#fff', fontSize: '1.2rem', letterSpacing: 2 }}>{roomId}</strong>
            </div>
            
            <button 
              className="btn btn-mode" 
              style={{ fontSize: '0.75rem', padding: '6px 12px', background: 'rgba(255,60,60,0.15)', color: '#ffaaaa', marginLeft: 0 }}
              onClick={leaveRoom}
            >
              مغادرة
            </button>
          </div>

          <img 
            src={`https://quickchart.io/qr?text=${encodeURIComponent(window.location.origin + '?room=' + roomId)}&size=150&margin=1`} 
            alt="QR Code" 
            style={{ width: 52, height: 52, borderRadius: 6, cursor: 'pointer', background: '#fff', padding: 2 }}
            onError={e => {
              // Fallback to alternate QR provider on failure
              if (!e.target.dataset.retry) {
                e.target.dataset.retry = '1';
                e.target.src = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(window.location.origin + '?room=' + roomId)}&margin=0`;
              }
            }}
            onClick={() => {
              const url = window.location.origin + '?room=' + roomId;
              navigator.clipboard.writeText(url);
              alert('تم نسخ رابط الجلسة!');
            }}
            title="اضغط لنسخ الرابط"
          />
        </div>

        {/* Center: Scores */}
        <div className="header-center">
          <div className="header-scores">
            <div className="score-badge orange">
              <span className="team-icon">🟠</span> البرتقالي: <strong>{scores.orange}</strong>
            </div>
            <div className="score-badge green">
              <span className="team-icon">🟢</span> الأخضر: <strong>{scores.green}</strong>
            </div>
            {mode !== '2p' && (
              <div className="score-badge blue">
                <span className="team-icon">🔵</span> الأزرق: <strong>{scores.blue}</strong>
              </div>
            )}
            {mode === '4p' && (
              <div className="score-badge purple">
                <span className="team-icon">🟣</span> البنفسجي: <strong>{scores.purple || 0}</strong>
              </div>
            )}
          </div>
        </div>

        {/* Left Side: Connection & Settings */}
        <div className="header-left">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9rem', fontWeight: 'bold', color: connected ? '#4caf50' : '#f44336', background: 'rgba(0,0,0,0.2)', padding: '8px 16px', borderRadius: 20 }}>
            <div className={`connection-dot ${!connected ? 'disconnected' : ''}`} />
            {connected ? 'متصل' : 'غير متصل'}
          </div>

          <button
            className="btn-size-toggle"
            onClick={() => {
              if (isAdmin) setShowSettingsModal(true);
              else setShowPinModal(true);
            }}
            title="الإعدادات (مخصصة للمدير)"
          >
            <span style={{ fontSize: '1.2rem' }}>⚙️</span> إعدادات
          </button>
        </div>

      </header>

      {/* ── Main Area ──────────────────────────────────────────────────────── */}
      <main className="main-area" style={{ gridTemplateColumns: '1fr', justifyItems: 'center' }}>
        <div className="grid-area">
          <div className="hex-grid-wrapper">
            {mode === '4p' || mode === '2p' ? (
              <HexGridRect
                mode={mode}
                grid={grid}
                onCellClick={handleCellClick}
                winPath={winPath}
                gridRadius={gridRadius}
              />
            ) : (
              <HexGrid
                grid={grid}
                onCellClick={handleCellClick}
                winPath={winPath}
                gridRadius={gridRadius}
              />
            )}
          </div>
        </div>
      </main>

      {/* ── Status Bar ─────────────────────────────────────────────────────── */}
      <footer className="status-bar">
        <span>{connected ? '✅ متصل بالخادم' : '❌ غير متصل بالخادم'}</span>
        <span style={{ color: 'var(--border)' }}>|</span>
        <span>
          {isAdmin ? '👑 وضع المدير — انقر على خلية لتلوينها' : '👀 وضع المشاهدة'}
        </span>
      </footer>

      {/* ── Context Menu ────────────────────────────────────────────────────── */}
      {ctxMenu && (
        <div
          ref={ctxRef}
          className="context-menu"
          style={{ 
            ...(ctxMenu.isNearRight
              ? { right: window.innerWidth - ctxMenu.x, left: 'auto' }
              : { left: ctxMenu.x, right: 'auto' }),
            ...(ctxMenu.isNearBottom 
              ? { bottom: window.innerHeight - ctxMenu.y, top: 'auto' } 
              : { top: ctxMenu.y, bottom: 'auto' })
          }}
        >
          <div className="context-menu-header">
            {currentCellColorName ? (
              <>
                <span>اللون الحالي:</span>
                <span
                  className="context-menu-dot"
                  style={{
                    background: COLOR_OPTIONS.find(c => c.id === ctxMenu.currentColor)?.hex,
                    borderColor: COLOR_OPTIONS.find(c => c.id === ctxMenu.currentColor)?.hex,
                    display: 'inline-block',
                    verticalAlign: 'middle',
                    margin: '0 4px',
                  }}
                />
                <strong style={{ color: COLOR_OPTIONS.find(c => c.id === ctxMenu.currentColor)?.hex }}>
                  {currentCellColorName}
                </strong>
              </>
            ) : (
              <span>بدون لون — اختر فريقاً</span>
            )}
          </div>

          <div className="context-menu-divider" />

          {validColorOptions.map(opt => (
            <button
              key={opt.id}
              className={`context-menu-item${ctxMenu.currentColor === opt.id ? ' selected' : ''} ${opt.isFocus ? 'focus-item' : ''}`}
              onClick={() => handleColorSelect(opt.id)}
              id={`color-${opt.id}`}
            >
              <span
                className="context-menu-dot"
                style={{
                  background: opt.isWhite ? 'transparent' : opt.hex,
                  borderColor: opt.isWhite ? '#aaa' : opt.hex,
                  boxShadow: opt.isWhite ? 'none' : `0 0 6px ${opt.hex}88`,
                }}
              />
              {opt.label}
              {ctxMenu.currentColor === opt.id && (
                <span className="context-menu-check">✓</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* ── Settings Modal ──────────────────────────────────────────────────── */}
      {showSettingsModal && isAdmin && (
        <div className="win-overlay" onClick={() => setShowSettingsModal(false)}>
          <div onClick={e => e.stopPropagation()} className="settings-scroll-wrapper" style={{ position: 'relative', maxHeight: '90vh', overflowY: 'auto', width: '95%', maxWidth: '400px', borderRadius: '12px', paddingRight: '5px' }}>
            <ControlPanel
              isAdminMode={isAdmin}
              onResetColors={handleResetColors}
              onResetGame={handleResetGame}
              connected={connected}
              gridRadius={gridRadius}
              onChangeGridSize={handleChangeGridSize}
              mode={mode}
              roomId={roomId}
              socket={socket}
            />
            <button
              className="btn btn-mode"
              style={{ marginTop: 12, width: '100%' }}
              onClick={() => setShowSettingsModal(false)}
            >
              إغلاق
            </button>
          </div>
        </div>
      )}

      {/* ── PIN Authentication Modal ────────────────────────────────────────── */}
      {showPinModal && (
        <div className="win-overlay" onClick={() => setShowPinModal(false)} style={{ zIndex: 9999 }}>
          <div className="size-modal" onClick={e => e.stopPropagation()} style={{ textAlign: 'center' }}>
            <h2 style={{ marginBottom: 15, fontSize: '1.2rem', color: '#FFB347' }}>تفعيل إدارة الجلسة</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: 20 }}>
              أدخل الرقم السري الذي ظهر لك عند إنشاء الجلسة حتى تتمكن من تلوين الخلايا وتصفير اللعبة.
            </p>
            <form onSubmit={handlePinSubmit}>
              <input
                type="number"
                placeholder="****"
                autoFocus
                value={pinInput}
                onChange={e => setPinInput(e.target.value)}
                style={{
                  background: '#111',
                  border: '1px solid #444',
                  color: '#fff',
                  padding: '12px',
                  fontSize: '1.5rem',
                  letterSpacing: '4px',
                  borderRadius: '8px',
                  textAlign: 'center',
                  width: '100%',
                  marginBottom: '15px'
                }}
              />
              <div style={{ display: 'flex', gap: '8px' }}>
                <button type="submit" className="btn btn-mode" style={{ background: '#2E8B57', color: '#fff', flex: 1 }}>
                  تأكيد
                </button>
                <button type="button" className="btn" onClick={() => setShowPinModal(false)} style={{ flex: 1 }}>
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Win Overlay ─────────────────────────────────────────────────────── */}
      {winEvent && (
        <div className="win-overlay" onClick={() => setWinEvent(null)}>
          <div
            className={`win-card ${winEvent.team}`}
            onClick={e => e.stopPropagation()}
          >
            <span className="win-emoji">🏆</span>
            <div className="win-title">
              {teamDisplayName[winEvent.team]}
            </div>
            <div className="win-subtitle">
              أتمّ مساراً متصلاً بين جانبيه المستهدفين!
            </div>
            <button
              className="btn btn-reset"
              style={{ maxWidth: 240, margin: '0 auto' }}
              onClick={() => setWinEvent(null)}
            >
              متابعة اللعبة
            </button>
          </div>
        </div>
      )}

      {/* ── Guest Buzzer UI ────────────────────────────────────────────────── */}
      {!isAdmin && !buzzerWinner && grid && isPlayer && (
        <div className="guest-buzzer-area" style={{ position: 'fixed', bottom: 0, left: 0, right: 0, padding: 20, background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)', textAlign: 'center' }}>
          {!myTeam ? (
            <>
              <h3 style={{ color: '#fff', marginBottom: 15, textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>اختر فريقك في تحدي زر السرعة (Buzzer)</h3>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                {validColorOptions.filter(o => o.id !== 'focus' && o.id !== 'white').map(o => (
                  <button key={o.id} onClick={() => setMyTeam(o.id)} style={{ padding: '15px 30px', borderRadius: 30, background: o.hex, color: '#fff', fontSize: '1.2rem', fontWeight: 'bold', border: 'none', cursor: 'pointer', boxShadow: '0 4px 10px rgba(0,0,0,0.5)' }}>
                    {o.label.split(' ')[1] || o.label}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <button
              onClick={() => socket.emit('buzz', { team: myTeam })}
              style={{
                width: '90%',
                maxWidth: 400,
                height: 120,
                borderRadius: 60,
                background: COLOR_OPTIONS.find(c => c.id === myTeam)?.hex,
                color: '#fff',
                fontSize: '2rem',
                fontWeight: 'bold',
                border: '4px solid white',
                cursor: 'pointer',
                boxShadow: '0 10px 30px rgba(0,0,0,0.8)',
                textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                transform: 'scale(1)',
                transition: 'transform 0.1s'
              }}
              onMouseDown={e => e.currentTarget.style.transform = 'scale(0.95)'}
              onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
              onTouchStart={e => { e.currentTarget.style.transform = 'scale(0.95)'; socket.emit('buzz', { team: myTeam }); }}
              onTouchEnd={e => e.currentTarget.style.transform = 'scale(1)'}
            >
              🚨 اضغط بسرعة! 🚨
            </button>
          )}
        </div>
      )}

      {/* ── Buzzer Winner Overlay ────────────────────────────────────────────── */}
      {buzzerWinner && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.85)', zIndex: 9999,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          color: '#fff', animation: 'fadeIn 0.3s'
        }}>
          <h1 style={{ fontSize: '4rem', textAlign: 'center', textShadow: `0 4px 50px ${COLOR_OPTIONS.find(c => c.id === buzzerWinner)?.hex}`, color: COLOR_OPTIONS.find(c => c.id === buzzerWinner)?.hex, margin: 0 }}>
            {TEAM_DISPLAY[buzzerWinner] || buzzerWinner} 🏆
          </h1>
          <p style={{ fontSize: '1.5rem', opacity: 0.8, marginTop: 10, marginBottom: 40 }}>هو الفريق الأسرع في الضغط!</p>
          {isAdmin && (
            <button
              onClick={() => socket.emit('reset_buzzer')}
              className="btn"
              style={{ padding: '20px 40px', fontSize: '1.5rem', background: '#ff5252', borderRadius: 30 }}
            >
              🔄 فتح وتصفير الجرس
            </button>
          )}
        </div>
      )}

    </div>
  );
}
